import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  ActivityIndicator,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../services/AuthContext';
import { createHotspot } from '../services/database';

const BulkImportHotspotsScreen = ({ navigation }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const { showToast } = useToast();
  const { appUser, userId } = useAuth();

  const sampleJSON = `[
  {
    "name": "Washington Square Park",
    "category": "PARK",
    "city": "New York",
    "description": "Famous chess destination with dozens of tables and players of all skill levels. Active daily, especially afternoons and weekends.",
    "address": "Washington Square Park, New York, NY 10012",
    "latitude": 40.7308,
    "longitude": -73.9975,
    "schedule": "Daily 10am-8pm",
    "activityLevel": "Very busy, peak afternoons",
    "amenities": ["Outdoor seating", "Free to play", "Spectator friendly"],
    "confidence": "CONFIRMED"
  },
  {
    "name": "Bryant Park Chess",
    "category": "PARK",
    "city": "New York",
    "description": "Beautiful park setting with chess tables. Less crowded than Washington Square, great for beginners.",
    "address": "Bryant Park, New York, NY 10018",
    "latitude": 40.7536,
    "longitude": -73.9832,
    "schedule": "Mon-Sun 9am-9pm",
    "activityLevel": "Moderate, busier on weekends",
    "amenities": ["Outdoor seating", "Beautiful setting", "Nearby cafes"],
    "confidence": "KNOWN_HOTSPOT"
  }
]`;

  const validateAndParseJSON = (input) => {
    try {
      // Clean the input: remove BOM, trim whitespace, fix common issues
      let cleanedInput = input.trim();
      
      // Remove BOM if present
      if (cleanedInput.charCodeAt(0) === 0xFEFF) {
        cleanedInput = cleanedInput.slice(1);
      }
      
      // Remove any leading/trailing whitespace or newlines
      cleanedInput = cleanedInput.trim();
      
      const data = JSON.parse(cleanedInput);
      
      if (!Array.isArray(data)) {
        throw new Error('JSON must be an array of hotspot objects');
      }

      // Validate each hotspot
      for (let i = 0; i < data.length; i++) {
        const hotspot = data[i];
        
        if (!hotspot.name || !hotspot.category || !hotspot.description || !hotspot.address) {
          throw new Error(`Hotspot ${i + 1}: Missing required fields (name, category, description, address)`);
        }
        
        if (!hotspot.latitude || !hotspot.longitude) {
          throw new Error(`Hotspot ${i + 1}: Missing coordinates (latitude, longitude)`);
        }

        const validCategories = ['PARK', 'CAFE', 'BAR', 'CLUB', 'UNIVERSITY', 'PUBLIC_SQUARE', 'OTHER'];
        if (!validCategories.includes(hotspot.category)) {
          throw new Error(`Hotspot ${i + 1}: Invalid category "${hotspot.category}". Must be one of: ${validCategories.join(', ')}`);
        }

        // Validate confidence if provided
        if (hotspot.confidence) {
          const validConfidences = ['CONFIRMED', 'KNOWN_HOTSPOT', 'RELATED'];
          if (!validConfidences.includes(hotspot.confidence)) {
            throw new Error(`Hotspot ${i + 1}: Invalid confidence "${hotspot.confidence}". Must be one of: ${validConfidences.join(', ')}`);
          }
        }
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleImport = async () => {
    if (!jsonInput.trim()) {
      showToast('Please enter JSON data', 'error');
      return;
    }

    const validation = validateAndParseJSON(jsonInput);
    if (!validation.success) {
      showToast(validation.error, 'error');
      return;
    }

    try {
      setImporting(true);
      const hotspots = validation.data;
      const results = { success: 0, failed: 0, errors: [] };

      // Get current user ID if available (use database UUID, not Clerk ID)
      let currentUserId = userId || appUser?.id || null;
      
      // Verify user is admin in database (for RLS policy)
      if (currentUserId) {
        try {
          const { getUserById } = require('../services/database');
          const dbUser = await getUserById(currentUserId);
        } catch (err) {
        }
      }

      for (let i = 0; i < hotspots.length; i++) {
        const hotspot = hotspots[i];
        
        try {
          // Map JSON fields to Supabase schema
          const hotspotData = {
            name: hotspot.name,
            description: hotspot.description,
            category: hotspot.category,
            city: hotspot.city || null,
            address: hotspot.address,
            latitude: parseFloat(hotspot.latitude),
            longitude: parseFloat(hotspot.longitude),
            schedule: hotspot.schedule || null,
            activity_level: hotspot.activityLevel || null,
            amenities: hotspot.amenities || null,
            photos: hotspot.photos || null,
            // Admin fields
            confidence: hotspot.confidence || 'RELATED',
            status: 'PUBLISHED', // Bulk imports are published immediately (admin-verified data)
            source: 'ADMIN',
            created_by: currentUserId,
          };

          await createHotspot(hotspotData);
          results.success++;
          console.log(`✅ Created: ${hotspot.name}`);
        } catch (error) {
          results.failed++;
          const errorMessage = error.message || error.toString();
          results.errors.push(`${hotspot.name}: ${errorMessage}`);
          console.error(`❌ Failed: ${hotspot.name}`, error);
        }
      }

      setImportResults(results);
      
      if (results.success > 0) {
        showToast(`Successfully imported ${results.success} hotspot(s)!`, 'success');
        if (results.failed === 0) {
          // Clear input on full success
          setJsonInput('');
        }
      } else {
        showToast('Import failed. Check results below.', 'error');
      }
    } catch (error) {
      console.error('Import error:', error);
      showToast('Import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleUseSample = () => {
    setJsonInput(sampleJSON);
    setImportResults(null);
  };

  const handleClear = () => {
    setJsonInput('');
    setImportResults(null);
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Bulk Import Hotspots</Text>
        <View style={{ width: 28 }} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Instructions */}
        <View style={styles.instructionsCard}>
          <Icon name="info" size={24} color="#2196F3" />
          <View style={styles.instructionsText}>
            <Text style={styles.instructionsTitle}>How to use:</Text>
            <Text style={styles.instructionsBody}>
              1. Get a list of hotspots from ChatGPT in JSON format{'\n'}
              2. Paste the JSON below{'\n'}
              3. Click "Import Hotspots"{'\n'}
              {'\n'}
              Tip: Click "Use Sample" to see the expected format{'\n'}
              {'\n'}
              💡 Optional fields:{'\n'}
              • "city" - enables city-based filtering{'\n'}
              • "confidence" - CONFIRMED, KNOWN_HOTSPOT, or RELATED (default: RELATED)
            </Text>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity style={styles.sampleButton} onPress={handleUseSample}>
            <Icon name="code" size={18} color="#2196F3" />
            <Text style={styles.sampleButtonText}>Use Sample</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.clearButton} onPress={handleClear}>
            <Icon name="clear" size={18} color="#f44336" />
            <Text style={styles.clearButtonText}>Clear</Text>
          </TouchableOpacity>
        </View>

        {/* JSON Input */}
        <View style={styles.inputSection}>
          <Text style={styles.label}>JSON Data</Text>
          <TextInput
            style={styles.jsonInput}
            value={jsonInput}
            onChangeText={setJsonInput}
            placeholder="Paste your JSON array here..."
            placeholderTextColor="#999"
            multiline
            numberOfLines={15}
          />
        </View>

        {/* Import Button */}
        <TouchableOpacity
          style={[styles.importButton, importing && styles.importButtonDisabled]}
          onPress={handleImport}
          disabled={importing}
        >
          {importing ? (
            <>
              <ActivityIndicator size="small" color="white" />
              <Text style={styles.importButtonText}>Importing...</Text>
            </>
          ) : (
            <>
              <Icon name="cloud-upload" size={24} color="white" />
              <Text style={styles.importButtonText}>Import Hotspots</Text>
            </>
          )}
        </TouchableOpacity>

        {/* Results */}
        {importResults && (
          <View style={styles.resultsCard}>
            <Text style={styles.resultsTitle}>Import Results</Text>
            <View style={styles.resultsStats}>
              <View style={styles.resultsStat}>
                <Icon name="check-circle" size={32} color="#4CAF50" />
                <Text style={styles.resultsStatNumber}>{importResults.success}</Text>
                <Text style={styles.resultsStatLabel}>Successful</Text>
              </View>
              <View style={styles.resultsStat}>
                <Icon name="error" size={32} color="#f44336" />
                <Text style={styles.resultsStatNumber}>{importResults.failed}</Text>
                <Text style={styles.resultsStatLabel}>Failed</Text>
              </View>
            </View>
            
            {importResults.errors.length > 0 && (
              <View style={styles.errorsSection}>
                <Text style={styles.errorsTitle}>Errors:</Text>
                {importResults.errors.map((error, index) => (
                  <Text key={index} style={styles.errorText}>• {error}</Text>
                ))}
              </View>
            )}
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  instructionsCard: {
    flexDirection: 'row',
    backgroundColor: '#E3F2FD',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
    gap: 12,
  },
  instructionsText: {
    flex: 1,
  },
  instructionsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1976D2',
    marginBottom: 8,
  },
  instructionsBody: {
    fontSize: 14,
    color: '#1565C0',
    lineHeight: 20,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  sampleButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#E3F2FD',
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2196F3',
  },
  sampleButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2196F3',
  },
  clearButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#FFEBEE',
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#f44336',
  },
  clearButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#f44336',
  },
  inputSection: {
    marginBottom: 16,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  jsonInput: {
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 10,
    padding: 16,
    fontSize: 13,
    fontFamily: 'monospace',
    color: '#333',
    height: 300,
    textAlignVertical: 'top',
  },
  importButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#94EB98',
    paddingVertical: 16,
    borderRadius: 10,
    marginBottom: 16,
  },
  importButtonDisabled: {
    opacity: 0.6,
  },
  importButtonText: {
    fontSize: 17,
    fontWeight: 'bold',
    color: 'white',
  },
  resultsCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  resultsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  resultsStats: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  resultsStat: {
    flex: 1,
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 10,
  },
  resultsStatNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 8,
  },
  resultsStatLabel: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  errorsSection: {
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    paddingTop: 16,
  },
  errorsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#f44336',
    marginBottom: 8,
  },
  errorText: {
    fontSize: 13,
    color: '#666',
    marginBottom: 4,
  },
});

export default BulkImportHotspotsScreen;

